/**
 * A draft is one stop's checklist while the driver is still standing there.
 * Every tap writes it - a photo lands in IndexedDB as a 'local' PhotoRecord
 * the moment it is compressed, a condition the moment it is tapped - so a
 * dead battery or a crash mid-stop loses nothing but the tap in flight.
 *
 * Pressing Complete (or Skip) turns the draft into a queued completion via
 * engine.enqueueFromLocal and clears the draft.
 */
import { deletePhoto, getMeta, listPhotosForStop, photoKey, putPhoto, setMeta } from './db'
import { enqueueFromLocal, requestBackgroundSync, drain } from './engine'
import type { ChecklistEntry, CompletionPayload, CompletionRecord, PhotoRecord } from './types'

export interface DraftItem {
  condition: string
  skipped: boolean
  captured_at: string | null
  hasPhoto: boolean
}

export interface StopDraft {
  stopUuid: string
  routeId: number
  startedAt: string
  arrivedAt: string | null
  arrivalLat: number | null
  arrivalLng: number | null
  items: Record<string, DraftItem>
  note: string
  /** Index of the checklist item the driver is on, so reopening resumes. */
  index: number
}

const draftKey = (uuid: string) => `draft:${uuid}`

export async function getDraft(uuid: string): Promise<StopDraft | undefined> {
  return getMeta<StopDraft>(draftKey(uuid))
}

/**
 * A position fix, once recorded, is never overwritten with nothing. The
 * arrival fix resolves in the background (DriverApp.startChecklist) while the
 * checklist screen keeps saving the draft it was handed, which predates the
 * fix - without this, the driver's first photo erased the arrival geotag.
 */
export async function saveDraft(draft: StopDraft): Promise<void> {
  const current = await getMeta<StopDraft>(draftKey(draft.stopUuid))
  const keepFix = current && draft.arrivalLat == null && current.arrivalLat != null
  await setMeta(
    draftKey(draft.stopUuid),
    keepFix ? { ...draft, arrivalLat: current.arrivalLat, arrivalLng: current.arrivalLng } : draft,
  )
}

export async function clearDraft(uuid: string): Promise<void> {
  await setMeta(draftKey(uuid), undefined)
}

export function newDraft(stopUuid: string, routeId: number, required: string[]): StopDraft {
  const items: Record<string, DraftItem> = {}
  for (const t of required) items[t] = { condition: '', skipped: false, captured_at: null, hasPhoto: false }
  return {
    stopUuid,
    routeId,
    startedAt: new Date().toISOString(),
    arrivedAt: null,
    arrivalLat: null,
    arrivalLng: null,
    items,
    note: '',
    index: 0,
  }
}

/** Rebuild a draft from a completion the driver wants to fix. Existing photos
 *  stay in their slots (and their sync state) until retaken. */
export async function draftFromCompletion(rec: CompletionRecord, required: string[]): Promise<StopDraft> {
  const draft = newDraft(rec.stopUuid, rec.routeId, required)
  const photos = await listPhotosForStop(rec.stopUuid)
  const have = new Set(photos.map((p) => p.itemType))
  for (const entry of rec.payload.checklist) {
    if (!(entry.item_type in draft.items)) continue
    draft.items[entry.item_type] = {
      condition: entry.condition,
      skipped: entry.skipped,
      captured_at: entry.captured_at,
      hasPhoto: have.has(entry.item_type),
    }
  }
  draft.arrivedAt = rec.payload.arrived_at
  draft.arrivalLat = rec.payload.arrival_lat
  draft.arrivalLng = rec.payload.arrival_lng
  draft.note = rec.payload.driver_note
  return draft
}

/** Store a compressed photo for an item while the checklist is in progress. */
export async function savePhotoLocal(stopUuid: string, itemType: string, blob: Blob, capturedAt: string): Promise<void> {
  const rec: PhotoRecord = {
    key: photoKey(stopUuid, itemType),
    stopUuid,
    itemType,
    blob,
    bytes: blob.size,
    capturedAt,
    state: 'local',
    attempts: 0,
    nextAttemptAt: Date.now(),
    lastError: '',
    syncedAt: null,
  }
  await putPhoto(rec)
}

export async function discardLocalPhoto(stopUuid: string, itemType: string): Promise<void> {
  await deletePhoto(photoKey(stopUuid, itemType))
}

export function payloadFromDraft(
  draft: StopDraft,
  status: 'complete' | 'skipped',
  actualSequence: number,
  skipReason = '',
): CompletionPayload {
  const checklist: ChecklistEntry[] = Object.entries(draft.items).map(([item_type, it]) => ({
    item_type,
    condition: it.condition,
    skipped: status === 'skipped' ? true : it.skipped || (!it.hasPhoto && !it.condition),
    captured_at: it.captured_at,
  }))
  return {
    status,
    skip_reason: skipReason,
    arrival_lat: draft.arrivalLat,
    arrival_lng: draft.arrivalLng,
    arrived_at: draft.arrivedAt,
    departed_at: new Date().toISOString(),
    driver_note: draft.note,
    actual_sequence: actualSequence,
    checklist,
  }
}

/** Complete or skip: one durable write, then the queue takes over. */
export async function finalizeDraft(
  draft: StopDraft,
  status: 'complete' | 'skipped',
  actualSequence: number,
  skipReason = '',
  options: { kick?: boolean } = {},
): Promise<CompletionRecord> {
  const payload = payloadFromDraft(draft, status, actualSequence, skipReason)
  const rec = await enqueueFromLocal(draft.stopUuid, draft.routeId, payload)
  await clearDraft(draft.stopUuid)
  if (options.kick !== false) {
    // Fire and forget: the record is durable already. Tests pass kick:false so
    // a background drain cannot outlive its test.
    void requestBackgroundSync()
    void drain({ source: 'foreground' })
  }
  return rec
}
